import { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from './contexts/AuthContext';
import { getLoginUrl } from './services/auth';
import AuthCallback from './pages/AuthCallback';
import AdminTenants from './pages/admin/AdminTenants';
import AdminTenantCreate from './pages/admin/AdminTenantCreate';
import AdminTenantDetail from './pages/admin/AdminTenantDetail';

const DRAWER_WIDTH = 220;

function ManagementGuard({ children }: { children: React.ReactNode }) {
  const { loading, user } = useAuth();

  useEffect(() => {
    if (!loading && !user) {
      window.location.replace(getLoginUrl());
    }
  }, [loading, user]);

  if (loading || !user) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user.isPlatformAdmin && !user.isPlatformSales) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Access denied.</Typography>
      </Box>
    );
  }

  return <>{children}</>;
}

function ManagementLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <Typography variant="h6" noWrap>
            Traaker — Management
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <Toolbar />
        <List sx={{ px: 1, pt: 1 }}>
          <ListItemButton
            onClick={() => navigate('/tenants')}
            selected={location.pathname.startsWith('/tenants')}
            sx={{ borderRadius: 2, mb: 0.5 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PeopleIcon />
            </ListItemIcon>
            <ListItemText primary="Tenants" primaryTypographyProps={{ fontSize: '0.875rem' }} />
          </ListItemButton>
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Toolbar />
        <Routes>
          <Route index element={<Navigate to="/tenants" replace />} />
          <Route path="tenants" element={<AdminTenants />} />
          <Route path="tenants/new" element={<AdminTenantCreate />} />
          <Route path="tenants/:tenantId" element={<AdminTenantDetail />} />
          <Route path="*" element={<Navigate to="/tenants" replace />} />
        </Routes>
      </Box>
    </Box>
  );
}

export default function ManagementApp() {
  return (
    <Routes>
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/*"
        element={
          <ManagementGuard>
            <ManagementLayout />
          </ManagementGuard>
        }
      />
    </Routes>
  );
}
